import { type Component, createMemo, Show, untrack } from "solid-js";
import { boolean, object } from "valibot";
import moreappIcon from "../../../assets/moreapp-icon-solid.svg";
import { TextInput } from "../../../components/inputs/TextInput";
import InputModal from "../../../components/modal/InputModal";
import { DECORATIVE_IMAGE_ALT, MAX_CHARACTERS_MD } from "../../../constants";
import ApiService from "../../../services/ApiService";
import { type Customer } from "../../../state/customer";
import type FolderId from "../../../state/FolderId";
import type FormId from "../../../state/FormId";
import { useTranslation } from "../../../state/i18n";
import type { CustomerInfo } from "../../../types";
import { require, requireText } from "../../../utils/validation/base";
import type FormVersionId from "../../../state/FormVersionId";
import { useNavigate } from "@solidjs/router";
import GenericFailureModal from "../../GenericFailureModal";
import { isNil } from "lodash-es";
import { customerReference, folderReference } from "../../../utils/validation/idReferences";
import { SingleSelectInput } from "../../../components/menu/Select";
import { useModalManager } from "../../../components/modal/ModalManager";
import type CustomerId from "../../../state/CustomerId";

type CopyFormModalProps = {
  formName: string;
  formId: FormId;
  folderId: FolderId;
  customer: Customer;
  customers: CustomerInfo[];
  formVersionId: FormVersionId;
};

const copyFormSchema = () =>
  object({
    name: requireText(MAX_CHARACTERS_MD),
    customerId: customerReference(),
    folderId: require(folderReference()),
    copyPermissions: boolean(),
  });

const CopyFormModal: Component<CopyFormModalProps> = (props) => {
  const { t } = useTranslation();
  const { replace } = useModalManager();
  const navigate = useNavigate();

  return (
    <InputModal
      variant="default"
      title={t(($) => $.COPY_FORM_MODAL_TITLE)}
      confirmLabel={t(($) => $.COPY_FORM_MODAL_ACTIONS_COPY)}
      cancelLabel={t(($) => $.COPY_FORM_MODAL_ACTIONS_CANCEL)}
      onConfirm={(value) => {
        ApiService.copyForm().mutate(
          untrack(() => ({
            name: value.name,
            destinationCustomerId: value.customerId,
            formId: props.formId,
            folderId: value.folderId,
            formVersionId: props.formVersionId,
            copyPermissions: value.copyPermissions,
            positionInFolder: 0,
          })),
          {
            onSuccess: () => {
              navigate(`/customers/${props.customer.customerId}/folders/${props.folderId}`);
            },
            onError: (err) => {
              replace({
                content: () => <GenericFailureModal title={t(($) => $.COPY_FORM_FAILURE_MODAL_TITLE)} error={err} />,
              });
            },
          },
        );
      }}
      initialValue={{
        name: `${t(($) => $.COPY_FORM_MODAL_FORM_TITLE_PREFIX)} ${props.formName}`,
        customerId: props.customer.customerId,
        folderId: props.folderId,
        copyPermissions: false,
      }}
      schema={copyFormSchema()}
    >
      {({ value, updateValue, error }) => {
        const foldersQuery = createMemo(() => {
          const customerId = value("customerId");

          if (isNil(customerId)) {
            return { isSuccess: false, data: [], isLoading: false };
          }

          return ApiService.getFoldersByCustomerId(customerId);
        });
        return (
          <div class="flex flex-col gap-4">
            <TextInput
              label={t(($) => $.COPY_FORM_MODAL_INPUT_NAME)}
              value={value("name") ?? ""}
              required
              onBlur={(e) => {
                updateValue("name", e.target.value);
              }}
              onInput={(e) => {
                updateValue("name", e.target.value);
              }}
              errorMessage={error("name")}
            />
            <Show when={props.customers.length > 1}>
              <SingleSelectInput<CustomerId>
                label={t(($) => $.COPY_FORM_MODAL_ACCOUNT_NAME)}
                options={props.customers.map((customer) => ({
                  variant: "image",
                  value: customer.customerId,
                  label: customer.name,
                  image: { src: moreappIcon, alt: DECORATIVE_IMAGE_ALT },
                  description: `${customer.customerId}`,
                }))}
                value={value("customerId")}
                onSelect={(customerId) => {
                  updateValue("customerId", customerId);
                  // reset folder selection
                  updateValue("folderId", props.customer.customerId.equals(customerId) ? props.folderId : null);
                }}
                sameWidth
              />
            </Show>
            <SingleSelectInput
              label={t(($) => $.COPY_FORM_MODAL_FOLDER_NAME)}
              required
              options={
                foldersQuery().isSuccess
                  ? (foldersQuery().data?.map((folder) => ({
                      variant: "icon",
                      value: folder.id.toString(),
                      label: folder.meta.name,
                      icon: {
                        name: folder.meta.icon,
                        type: "solid",
                      },
                    })) ?? [])
                  : []
              }
              value={value("folderId")?.toString() ?? null}
              errorMessage={error("folderId")}
              onSelect={(folderId) => updateValue("folderId", folderId)}
              sameWidth
            />
            {/* TODO: DEV-6991 - uncomment this when ticket implemented
                <Checkbox
                checked={value("copyPermissions")}
                setChecked={(value) => updateValue("copyPermissions", value)}
                disabled={!props.customer.customerId.equals(value("customerId"))}
                label={t($ => $.COPY_FORM_MODAL_DUPLICATE_PERMISSIONS)}
                info={{
                  title: t($ => $.COPY_FORM_MODAL_DUPLICATE_PERMISSIONS_INFO_TITLE),
                  alt: t($ => $.COPY_FORM_MODAL_DUPLICATE_PERMISSIONS_INFO_TITLE),
                  description: t($ => $.COPY_FORM_MODAL_PERMISSIONS_INFO_DESCRIPTION),
                }}
                block
              /> */}
          </div>
        );
      }}
    </InputModal>
  );
};

export default CopyFormModal;
